import { ComponentType, ReactNode, useMemo } from 'react';

import { ReferralColumnKey } from 'components/insights/careTransitions/referrals/table/referralDetailsColumns';
import { Dimensions } from 'components/insights/constants';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import {
  getSlideoutEntityFilter,
  MetricDrillKey,
  useReferralsInsightsActions,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';

import SlideoutShell, { BorderedSection } from '../../shared/slideout/SlideoutShell';
import {
  BASE_EXPORT_METRICS,
  DIMENSION_EXPORT_SLUGS,
  getReferralExportParams,
  METRIC_DRILL_EXPORT_METRICS,
  METRIC_DRILL_VIEW_BY_OPTIONS,
} from '../constants';
import ReferralApiExport from '../ReferralApiExport';
import { useReferralsInsightsContext } from '../ReferralsContainer';

import AcceptanceRateDetailHeader from './AcceptanceRateDetailHeader';
import DenialRateDetailHeader from './DenialRateDetailHeader';
import PatientListSection from './PatientListSection';
import ReferralInsightsTable from './ReferralInsightsTable';
import ResponseTimeDetailHeader from './ResponseTimeDetailHeader';
import SummaryDetailHeader from './SummaryDetailHeader';
import useSlideoutAnalytics from './useSlideoutAnalytics';

const METRIC_DRILL_TITLES: Record<MetricDrillKey, string> = {
  response_time: 'Avg. Response Time - Overview',
  denial_rate: 'Denial Rate - Overview',
  acceptance_rate: 'Acceptance Rate - Overview',
};

const METRIC_DRILL_EXPORT_SLUGS: Record<MetricDrillKey, string> = {
  response_time: 'response-time',
  denial_rate: 'denial-rate',
  acceptance_rate: 'acceptance-rate',
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const METRIC_DRILL_DEFAULT_SORT: Record<MetricDrillKey, { id: string; desc: boolean }> = {
  response_time: { id: 'avgResponseTime', desc: true },
  denial_rate: { id: 'deniedRate', desc: true },
  acceptance_rate: { id: 'acceptedRate', desc: true },
};

const METRIC_DRILL_VISIBLE_COLUMNS: Record<MetricDrillKey, readonly ReferralColumnKey[]> = {
  response_time: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.AVG_RESPONSE_TIME,
  ],
  denial_rate: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.DENIED_RATE,
  ],
  acceptance_rate: [
    ReferralColumnKey.ENTITY,
    ReferralColumnKey.REFERRALS,
    ReferralColumnKey.UNIQUE_PATIENTS,
    ReferralColumnKey.ACCEPTED_RATE,
    ReferralColumnKey.AVG_DAYS_TO_START_CARE,
    ReferralColumnKey.AVG_ACCEPTED_TO_DISCHARGE,
  ],
};

type MetricHeaderProps = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

const METRIC_HEADERS: Record<MetricDrillKey, ComponentType<MetricHeaderProps>> = {
  response_time: ResponseTimeDetailHeader,
  denial_rate: DenialRateDetailHeader,
  acceptance_rate: AcceptanceRateDetailHeader,
};

function renderMetricHeader(metric: MetricDrillKey, props: MetricHeaderProps): ReactNode {
  const Header = METRIC_HEADERS[metric];
  return <Header {...props} />;
}

export default function ReferralInsightsSlideout() {
  const { predecessorGroupType } = useReferralsInsightsContext();
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);
  const slideoutDimension = useReferralsInsightsStore((state) => state.slideoutDimension);
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const startDate = useReferralsInsightsStore((state) => state.filters[START_DATE]) as Date | undefined;
  const endDate = useReferralsInsightsStore((state) => state.filters[END_DATE]) as Date | undefined;
  const { closeSlideout } = useReferralsInsightsActions();

  const extraFilters = useMemo(
    () => (slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null),
    [slideoutContext]
  );

  const {
    activeRollups: rollups,
    activeLoading: loading,
    sendingCount,
    receivingCount,
    countsLoading,
  } = useSlideoutAnalytics();

  const predecessorGroupTypeName = predecessorGroupType?.displayName ?? 'SNF';
  const receivingGroupTypeName = selectedReceivingGroupType?.displayName ?? '';

  const isDrillingIntoSnf =
    slideoutContext?.kind === 'entity' && slideoutContext.dimension === Dimensions.SENDING_GROUP;

  let title = '';
  let subtitle = '\u00A0';
  let subtitleReady = false;

  if (slideoutContext?.kind === 'entity') {
    title = `${slideoutContext.entityName} Overview`;
    subtitleReady = !countsLoading;
    subtitle = isDrillingIntoSnf
      ? `Referrals to ${receivingCount} ${receivingGroupTypeName}s`
      : `Referrals from ${sendingCount} ${predecessorGroupTypeName}s`;
  } else if (slideoutContext?.kind === 'metric') {
    title = METRIC_DRILL_TITLES[slideoutContext.metric];
    subtitleReady = !countsLoading;
    subtitle = `${sendingCount} ${predecessorGroupTypeName}s • ${receivingCount} ${receivingGroupTypeName}s`;
  }

  const open = slideoutContext !== null;

  const headerContent =
    slideoutContext?.kind === 'metric' ? (
      renderMetricHeader(slideoutContext.metric, { loading, rollups })
    ) : slideoutContext ? (
      <SummaryDetailHeader loading={loading} rollups={rollups} />
    ) : null;

  const tableViewByOptions = slideoutContext?.kind === 'metric' ? METRIC_DRILL_VIEW_BY_OPTIONS : undefined;
  const tableDefaultSort =
    slideoutContext?.kind === 'metric' ? METRIC_DRILL_DEFAULT_SORT[slideoutContext.metric] : undefined;
  const tableVisibleColumns =
    slideoutContext?.kind === 'metric' ? METRIC_DRILL_VISIBLE_COLUMNS[slideoutContext.metric] : undefined;

  const exportMetrics =
    slideoutContext?.kind === 'metric' ? METRIC_DRILL_EXPORT_METRICS[slideoutContext.metric] : BASE_EXPORT_METRICS;
  const exportParams = useMemo(
    () => (slideoutDimension ? getReferralExportParams(slideoutDimension, exportMetrics) : null),
    [slideoutDimension, exportMetrics]
  );
  const exportFilename = useMemo(() => {
    if (!slideoutContext || !slideoutDimension) return undefined;
    const viewBySlug = DIMENSION_EXPORT_SLUGS[slideoutDimension];
    const viewBySuffix = viewBySlug ? `-by-${viewBySlug}` : '';
    if (slideoutContext.kind === 'metric') {
      return `referral-coordination-${METRIC_DRILL_EXPORT_SLUGS[slideoutContext.metric]}${viewBySuffix}`;
    }
    return `referral-coordination-${slugify(slideoutContext.entityName)}${viewBySuffix}`;
  }, [slideoutContext, slideoutDimension]);

  return (
    <SlideoutShell
      open={open}
      onClose={closeSlideout}
      title={title}
      subtitle={subtitle}
      subtitleReady={slideoutContext ? subtitleReady : false}
      startDate={startDate}
      endDate={endDate}>
      {slideoutContext && (
        <>
          {headerContent}
          <BorderedSection>
            <ReferralInsightsTable
              key={
                slideoutContext.kind === 'metric'
                  ? `metric:${slideoutContext.metric}`
                  : `entity:${slideoutContext.entityId}`
              }
              loading={loading}
              rollups={rollups}
              viewByOptions={tableViewByOptions}
              defaultSort={tableDefaultSort}
              visibleColumns={tableVisibleColumns}
              exportSlot={
                exportParams && (
                  <ReferralApiExport
                    params={exportParams}
                    filename={exportFilename}
                    extraFilters={extraFilters}
                    disabled={loading}
                  />
                )
              }
            />
          </BorderedSection>
          <PatientListSection />
        </>
      )}
    </SlideoutShell>
  );
}
